import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const STORAGE_KEY = "stockly_login_remember";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const t = useT();

  // Load remembered credentials if any
  const remembered = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
  })();

  const [email, setEmail] = useState(remembered?.email || "");
  const [password, setPassword] = useState(remembered?.password || "");
  const [remember, setRemember] = useState(!!remembered);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [twofaRequired, setTwofaRequired] = useState(false);
  const [twofaCode, setTwofaCode] = useState("");
  const [showPw, setShowPw] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const result = await login(email, password, twofaCode);
      if (result?.twofa_required) {
        setTwofaRequired(true);
        setLoading(false);
        return;
      }
      if (remember) localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, password }));
      else localStorage.removeItem(STORAGE_KEY);
      toast.success(t("welcome_back"));
      const user = result;
      if (user?.role === "super_admin" || user?.role === "platform_owner") nav("/platform");
      else if (user?.role === "support") nav("/support");
      else if (user?.role === "cybersecurity") nav("/cyber");
      else nav("/");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1fr_1.1fr]" data-testid="login-page">
      <div className="flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-sm">
          <img src="/stockly-logo-tight.png" alt="Stockly" className="w-52 sm:w-64 md:w-[340px] h-auto mb-6 sm:mb-8" data-testid="login-logo" />

          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 text-[#1B2D5C]">{t("welcome_back")}</h1>

          <form onSubmit={onSubmit} className="space-y-4 mt-6" data-testid="login-form">
            <div>
              <label className="overline block mb-2">{t("email")}</label>
              <input type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="overline block mb-2">{t("password")}</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 pl-3 pr-11 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                  data-testid="login-password-input"
                />
                <button type="button" onClick={() => setShowPw((v) => !v)} tabIndex={-1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 inline-flex items-center justify-center text-zinc-400 hover:text-[#1B2D5C] rounded"
                  aria-label={showPw ? "Wachtwoord verbergen" : "Wachtwoord tonen"}
                  data-testid="toggle-password-visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <label className="inline-flex items-center justify-between gap-2 text-sm text-zinc-600 w-full">
              <span className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
                <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#5BC044] w-4 h-4" data-testid="remember-me-checkbox" />
                {t("remember_me")}
              </span>
              <Link to="/wachtwoord-vergeten" className="text-[#5BC044] hover:underline" data-testid="forgot-password-link">
                Wachtwoord vergeten?
              </Link>
            </label>

            {twofaRequired && (
              <div data-testid="twofa-section">
                <label className="overline block mb-2">Authenticator-code (6 cijfers)</label>
                <input
                  autoFocus
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={twofaCode}
                  onChange={(e) => setTwofaCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full h-11 px-3 border border-zinc-300 rounded-md text-sm font-mono tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-[#5BC044]/45 focus:border-[#5BC044]"
                  placeholder="000000"
                  data-testid="twofa-code-input"
                />
                <p className="text-xs text-zinc-500 mt-1.5">Open je authenticator app (Google Authenticator, 1Password, Authy) en voer de code in.</p>
              </div>
            )}

            {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}

            <button type="submit" disabled={loading}
              className="w-full h-11 bg-[#5BC044] hover:bg-[#4FAA3A] disabled:opacity-60 text-white rounded-md font-semibold inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? "…" : t("login")}
              <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-8 text-sm text-zinc-500">
            {t("no_account")} <Link to="/register" className="text-[#5BC044] font-medium hover:underline" data-testid="goto-register">{t("register")}</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1594394490830-4cf54dd62910?w=1600&q=80)" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B2D5C]/85 via-[#1B2D5C]/55 to-[#5BC044]/40" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="overline text-white/70">Sinds 2026</div>
          <div className="max-w-md">
            <div className="text-5xl font-bold tracking-tight leading-[1.05] mb-4">
              {t("hero_one_system")}<br />{t("hero_each_resto")}<br />
              <span className="text-[#A0E07F]">{t("hero_no_hassle")}</span>
            </div>
            <div className="text-white/80 text-sm leading-relaxed">
              Realtime inzicht in voorraad, leveranciers en bestellingen.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
